import { format, formatDistanceToNow } from 'date-fns'; // Import date-fns for formatting

/**
 * Formats an ISO date string or Date object into a readable format.
 * Example: '2023-10-27T10:30:00Z' -> '10/27/23 10:30' (compact) or '2 hours ago' (relative)
 * @param dateInput Date object or ISO string
 * @param formatType 'compact', 'full', or 'relative'
 * @returns Formatted date string or 'Invalid Date'
 */
export const formatDateTime = (
  dateInput: string | Date | undefined | null,
  formatType: 'compact' | 'full' | 'relative' = 'compact' // Default to compact
): string => {
  if (!dateInput) return '-'; // Handle null/undefined input

  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    // Check if the date is valid after parsing
    if (isNaN(date.getTime())) {
      console.warn("Invalid date input for formatting:", dateInput);
      return 'Invalid Date';
    }

    if (formatType === 'relative') {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    let formatString: string;
    switch (formatType) {
      case 'full':
        // Example: 10/27/2023, 10:30:00 AM
        formatString = 'MM/dd/yyyy, hh:mm:ss a';
        break;
      case 'compact':
      default:
        // Example: 10/27 10:30
        formatString = 'MM/dd HH:mm';
        break;
    }
    return format(date, formatString);

  } catch (error) {
    console.error("Error formatting date:", dateInput, error);
    return 'Formatting Error';
  }
};

/**
 * Safely formats a number to a specified precision, handling potential non-numeric inputs.
 * @param value The number or string to format.
 * @param precision The number of decimal places.
 * @returns Formatted number string or '-' if input is invalid.
 */
export const formatNumber = (
  value: number | string | undefined | null,
  precision: number
): string => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) {
    // console.warn('Invalid number input for formatting:', value);
    return '-'; // Return '-' or perhaps the original value if preferred
  }
  // Ensure precision is a non-negative integer
  const safePrecision = Math.max(0, Math.floor(precision));
  return num.toFixed(safePrecision);
};

/**
 * Formats a price value using a given precision.
 * @param price The price value.
 * @param precision The number of decimal places for price formatting.
 * @returns Formatted price string.
 */
export const formatPrice = (
  price: number | string | undefined | null,
  precision: number
): string => {
  // TODO: Potentially add currency symbols or specific styling later
  return formatNumber(price, precision);
};

/**
 * Formats a size value using a given precision.
 * @param size The size value.
 * @param precision The number of decimal places for size formatting.
 * @returns Formatted size string.
 */
export const formatSize = (
  size: number | string | undefined | null,
  precision: number
): string => {
  return formatNumber(size, precision);
};

/**
 * Formats a fee value, typically with higher precision.
 * @param fee The fee amount.
 * @param currency The currency symbol (e.g., 'USDC').
 * @param precision The number of decimal places (default: 4).
 * @returns Formatted fee string with currency.
 */
export const formatFee = (
  fee: number | string | undefined | null,
  currency: string | undefined | null,
  precision: number = 4 // Default precision for fees
): string => {
  const formattedNumber = formatNumber(fee, precision);
  if (formattedNumber === '-') return '-';
  return `${formattedNumber} ${currency || ''}`.trim(); // Append currency if available
};

// Add other formatters here as needed (e.g., formatCurrency, formatNumber) 